import { BoxProps } from "@mui/material";
import { useEffect, useRef } from "react";

import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useOnboardingContext } from "@/common/contexts/onboarding";
import { noop } from "@/common/helpers/function";
import { RoutingHelper } from "@/common/helpers/routing";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { useQueryParams } from "@/common/hooks/useQueryParams";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { GeneralQueryParams } from "@/common/ts/GeneralQueryParams";
import { ViewVariant } from "@/common/ts/layout";
import { assertNever } from "@/common/ts/typeHelpers";
import {
  EntityType,
  OnboardingDto,
  OnboardingStatus,
  OnboardingStepType,
} from "@/core/api/generated/v1";

import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { OnboardingCompletedView } from "./OnboardingCompletedView";
import { OnboardingIntroView } from "./OnboardingIntroView";
import { OnboardingStepView } from "./OnboardingStepView";
import { OnboardingStepsView } from "./OnboardingStepsView";

export const OnboardingSubViewType = {
  Intro: "intro",
  Steps: "steps",
  StepDetails: "step-details",
  Completed: "completed",
} as const;
export type OnboardingSubViewType =
  (typeof OnboardingSubViewType)[keyof typeof OnboardingSubViewType];

export const commonViewContainerSx: BoxProps["sx"] = {
  maxWidth: undefined,
};

export interface OnboardingViewQueryParams extends GeneralQueryParams {
  subViewType?: OnboardingSubViewType;
  stepDefinitionId?: string;
  stepType?: OnboardingStepType;
}

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

interface OwnProps extends BaseEntityViewInheritableProps<OnboardingDto> {
  onboardingId: string | null | undefined;
  onboarding: OnboardingDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export type OnboardingViewProps = OwnProps;

export default function OnboardingView({
  onboardingId,
  onboarding,
  displayProps = defaultDisplayProps,
  onUpdate,
  onClose,
}: OnboardingViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const queryParams = useQueryParams<OnboardingViewQueryParams>({
    mode: "current",
  });

  // const currentTenant = useCurrentTenant();
  const profile = useUserProfile();
  const onboardingContext = useOnboardingContext();

  // onboardingContext.fetchForProfileIfNotFetched(profile);

  const isIniting = !profile || onboardingContext.isIniting;
  const onboardingIdComputed = onboardingId || profile?.onboarding?.id;
  const isGoHome = !onboardingContext?.isEnabled || (!isIniting && !onboardingIdComputed);

  onboarding = onboarding || onboardingContext.onboarding;

  const subViewTypeInferred = !onboarding
    ? undefined
    : onboarding.status === OnboardingStatus.NotStarted
      ? OnboardingSubViewType.Intro
      : OnboardingSubViewType.Steps;
  const subViewTypeComputed =
    queryParams.subViewType || subViewTypeInferred || OnboardingSubViewType.Intro;

  // close popover
  const isPopperClosedInitiallyRef = useRef(false);
  if (onboardingContext.popper.isOpen && !isPopperClosedInitiallyRef.current) {
    onboardingContext.popper.close();
  }
  isPopperClosedInitiallyRef.current = true;

  useEffect(() => {
    if (onboardingIdComputed) {
      onboardingContext.fetchById(onboardingIdComputed);
    }
  }, [onboardingIdComputed]);

  useEffect(() => {
    if (!isIniting) {
      onboardingContext.handleOnboardingViewed();
    }
  }, [isIniting]);

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps?.breadcrumbs &&
        onboarding && {
          idValue: onboarding.id!,
          newTitle: onboarding.globalNumber || "",
        }) ||
      undefined,
  });

  if (isGoHome) {
    history.push(RoutingHelper.getCurrentAppAreaRootPathOrDefault());
    return null;
  }

  return (
    <BaseEntityView
      entityType={EntityType.Onboarding}
      entityId={onboardingId}
      entity={onboarding}
      entityRequest={undefined}
      isIniting={isIniting}
    >
      {onboarding && (
        <>
          {(() => {
            switch (subViewTypeComputed) {
              case OnboardingSubViewType.Intro:
                // NB: must render at view layout root level to ensure 100% height for centering
                return <OnboardingIntroView onDelete={noop} />;
              case OnboardingSubViewType.Steps:
                return <OnboardingStepsView onDelete={noop} />;
              case OnboardingSubViewType.StepDetails:
                return (
                  <OnboardingStepView
                    stepDefinitionId={queryParams.stepDefinitionId}
                    stepType={queryParams.stepType}
                    onDelete={noop}
                  />
                );
              case OnboardingSubViewType.Completed:
                // NB: must render at view layout root level to ensure 100% height for centering
                return <OnboardingCompletedView onDelete={noop} />;
              default:
                assertNever(subViewTypeComputed);
            }
          })()}
        </>
      )}
    </BaseEntityView>
  );
}
